import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Nullable } from "@jamesgmarks/utilities";
import { ClientDropdown } from "src/components/parts/ClientDropdown";
import { useAppSelector } from "src/redux/hooks";
import { AutoDisableButton } from "src/components/parts/AutoDisableButton";
import { performApiActionRequest } from "src/redux/apiActionRequest";
import { IApiQueryResponse } from "@llws/api-common";
import { ITokenData } from "src/customHooks/ITokenData";
import { getMany } from "src/redux/features/dynamic/actions";
import { IBaseSubscriptions } from "@llws/lift-entity-interfaces";

/**
 * Shape of the `data` payload returned by the `updatePromotedListingPlanForClient` action
 * for both dry-run previews and actual migrations.
 */
type TUpdatePlActionData = {
  clientId: number;
  processedContractSubscriptionIds: number[];
  processedListingSubscriptionIds: number[];
  processedLiftSettingsIds: number[];
  /** The old→new base subscription ID migration map applied during this run. */
  migrationMap: Record<number, number>;
};

/**
 * Envelope returned by the `updatePromotedListingPlanForClient` API action.
 * Wraps the success flag, status message, optional result data, and any error text.
 */
type TUpdatePlActionResponse = {
  actionResponse: {
    success: boolean;
    message: string;
    data?: TUpdatePlActionData;
    error?: string;
  };
};

const BASE_SUBSCRIPTION_MIGRATION_OLD_IDS: number[] = [
  357, 359, 349, 407, 408, 409, 410,
];

/** Display data for a single old contract base subscription chip in the selection UI. */
type TChipLabelData = {
  id: number;
  label: string;
};

/**
 * Truncates a chip label to at most `maxLength` characters, appending `…` when trimmed.
 * Keeps chip widths predictable while preserving the full label in the tooltip.
 *
 * @param label - The full label string to shorten.
 * @param maxLength - Maximum number of characters to keep (default: 48).
 * @returns The original label if it fits, otherwise a truncated version ending with `…`.
 */
const toShortChipLabel = (label: string, maxLength: number = 48) => (
  label.length > maxLength
    ? `${label.slice(0, maxLength - 1)}...`
    : label
);

/**
 * Finance Control Tool – Update Promoted Listing Plan For Client.
 *
 * Provides a two-phase UI for migrating a client's promoted listing subscriptions
 * from legacy pricing plans to updated plans:
 *
 * 1. **Dry-run preview** – triggered by the initial confirm dialog; calls the
 *    `updatePromotedListingPlanForClient` action with `dryRun: true` and shows the
 *    user a count of affected contract and listing subscriptions before any writes occur.
 * 2. **Live execution** – triggered from the preview dialog; re-invokes the action
 *    with `dryRun: false` to commit the database changes.
 *
 * The operator must select a client and at least one old contract base subscription ID
 * (from `BASE_SUBSCRIPTION_MIGRATION_OLD_IDS`) before the action button is enabled.
 */
export const UpdatePromotedListingPlanForClient = () => {
  const [clientId, setClientId] = useState<Nullable<number>>(null);
  const [selectedOldBaseSubscriptionIds, setSelectedOldBaseSubscriptionIds] = useState<number[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultContent, setResultContent] = useState<{ success: boolean; text: string }>({
    success: false,
    text: '',
  });
  const [previewContent, setPreviewContent] = useState<TUpdatePlActionData | null>(null);

  const token = useAppSelector((state) => state.auth?.decodedToken);
  const searchIdFilter = useAppSelector((state) => state.clients.filters.searchIdFilter);
  const baseSubscriptions = useAppSelector(
    (state) => (state.dynamic.data.base_subscriptions?.list ?? []) as IBaseSubscriptions[],
  );
  const baseSubscriptionsLoadedState = useAppSelector(
    (state) => state.dynamic.data.base_subscriptions?.loadedState,
  );
  const isBaseSubscriptionsLoading = baseSubscriptionsLoadedState !== 'loaded';

  useEffect(() => {
    getMany('base_subscriptions', {
      relations: [
        { path: 'partner', alias: 'p' },
      ],
      limit: 9999,
    });
  }, []);

  const chipLabelData = useMemo<TChipLabelData[]>(() => {
    const baseSubscriptionById = new Map<number, IBaseSubscriptions>(
      baseSubscriptions
        .filter((baseSubscription) => typeof baseSubscription.id === 'number')
        .map((baseSubscription) => [baseSubscription.id as number, baseSubscription]),
    );

    return BASE_SUBSCRIPTION_MIGRATION_OLD_IDS.map((oldBaseSubscriptionId) => {
      const baseSubscription = baseSubscriptionById.get(oldBaseSubscriptionId);
      const invoiceDescription = baseSubscription?.invoiceDescription ?? 'Unknown invoice description';
      const partnerName = baseSubscription?.partner?.name ?? 'Unknown partner';
      return {
        id: oldBaseSubscriptionId,
        label: `${oldBaseSubscriptionId} - ${invoiceDescription} - ${partnerName}`,
      };
    });
  }, [baseSubscriptions]);

  const toggleOldBaseSubscriptionId = (oldBaseSubscriptionId: number) => {
    setSelectedOldBaseSubscriptionIds((current) => (
      current.includes(oldBaseSubscriptionId)
        ? current.filter((id) => id !== oldBaseSubscriptionId)
        : [...current, oldBaseSubscriptionId]
    ));
  };

  /**
   * Phase 1 handler – closes the confirmation dialog, then fires a dry-run API call.
   * On success, populates the preview dialog with the projected record counts.
   * On failure, surfaces the error in the result dialog instead.
   */
  const handleConfirm = async () => {
    setConfirmOpen(false);
    try {
      // First call: dry-run to preview changes
      const response = await performApiActionRequest<TUpdatePlActionResponse>(
        'updatePromotedListingPlanForClient',
        {
          clientId,
          token: token as ITokenData,
          selectedOldBaseSubscriptionIds,
          dryRun: true,
        },
      ) as IApiQueryResponse<TUpdatePlActionResponse>;

      const responseData = Array.isArray(response?.data) ? response.data[0] : response?.data;
      const actionResponse = responseData?.actionResponse;
      const success = actionResponse?.success ?? false;

      if (!success || actionResponse?.message === 'error') {
        setResultContent({
          success: false,
          text: actionResponse?.error ?? actionResponse?.message ?? 'An unknown error occurred.',
        });
        setResultOpen(true);
      } else {
        // Show preview modal with results
        setPreviewContent(actionResponse?.data ?? null);
        setPreviewOpen(true);
      }
    } catch (e) {
      setResultContent({
        success: false,
        text: e instanceof Error ? e.message : 'An unknown error occurred.',
      });
      setResultOpen(true);
    }
  };

  /**
   * Phase 2 handler – closes the preview dialog, then fires the live migration API call
   * (`dryRun: false`). Displays a success summary or error message in the result dialog.
   */
  const handleProceed = async () => {
    setPreviewOpen(false);
    try {
      // Second call: actual execution with dry-run: false
      const response = await performApiActionRequest<TUpdatePlActionResponse>(
        'updatePromotedListingPlanForClient',
        {
          clientId,
          token: token as ITokenData,
          selectedOldBaseSubscriptionIds,
          dryRun: false,
        },
      ) as IApiQueryResponse<TUpdatePlActionResponse>;

      const responseData = Array.isArray(response?.data) ? response.data[0] : response?.data;
      const actionResponse = responseData?.actionResponse;
      const success = actionResponse?.success ?? false;

      if (!success || actionResponse?.message === 'error') {
        setResultContent({
          success: false,
          text: actionResponse?.error ?? actionResponse?.message ?? 'An unknown error occurred.',
        });
      } else {
        const contractCount = actionResponse?.data?.processedContractSubscriptionIds?.length ?? 0;
        const listingCount = actionResponse?.data?.processedListingSubscriptionIds?.length ?? 0;
        const contractLabel = `contract subscription${contractCount !== 1 ? 's' : ''}`;
        const listingLabel = `child listing subscription${listingCount !== 1 ? 's' : ''}`;

        setResultContent({
          success: true,
          text: `Updated ${contractCount} ${contractLabel} and ${listingCount} ${listingLabel} for client ${clientId}.`,
        });
      }
    } catch (e) {
      setResultContent({
        success: false,
        text: e instanceof Error ? e.message : 'An unknown error occurred.',
      });
    }
    setResultOpen(true);
  };

  return (
    <>
      <h3>Update Promoted Listing Plan For Client</h3>
      <p>
        Migrates all matching promoted listing subscriptions for a client to the new plan
        as per the configured migration map.
      </p>
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <ClientDropdown
            allowFreeform
            clientId={searchIdFilter ?? null}
            onClientChanged={(client) => {
              if (client) {
                setClientId(client.id);
              }
            }}
            onClientFreeForm={(_clientString) => {}}
          />
        </Grid>
        <Grid item xs={8}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Select old contract base IDs to update
            </Typography>
            {isBaseSubscriptionsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading base subscription details...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {BASE_SUBSCRIPTION_MIGRATION_OLD_IDS.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No migration IDs configured yet.
                  </Typography>
                )}
                {chipLabelData.map((chipData) => {
                  const selected = selectedOldBaseSubscriptionIds.includes(chipData.id);

                  return (
                    <Tooltip key={chipData.id} title={chipData.label} arrow>
                      <Chip
                        label={toShortChipLabel(chipData.label)}
                        clickable
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => toggleOldBaseSubscriptionId(chipData.id)}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={8}>
          <AutoDisableButton
            variant="contained"
            disabled={!clientId || selectedOldBaseSubscriptionIds.length === 0 || isBaseSubscriptionsLoading}
            onClick={() => setConfirmOpen(true)}
          >
            Update Promoted Listing Plan
          </AutoDisableButton>
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Plan Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update the promoted listing plan for client <strong>{clientId}</strong>?
            This will modify subscriptions and lift settings in the database.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected old contract base IDs
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedOldBaseSubscriptionIds.map((oldBaseSubscriptionId) => {
                const selectedChipData = chipLabelData.find((chipData) => chipData.id === oldBaseSubscriptionId);
                const fullLabel = selectedChipData?.label ?? `${oldBaseSubscriptionId}`;
                return (
                  <Tooltip key={oldBaseSubscriptionId} title={fullLabel} arrow>
                    <Chip
                      label={toShortChipLabel(fullLabel)}
                      size="small"
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirm}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <DialogTitle>Preview Update Results</DialogTitle>
        <DialogContent>
          {previewContent && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This preview shows what will be updated if you proceed:
              </Typography>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2">
                  Contract Subscriptions: <strong>{previewContent.processedContractSubscriptionIds?.length ?? 0}</strong>
                </Typography>
                <Typography variant="subtitle2">
                  Child Listing Subscriptions: <strong>{previewContent.processedListingSubscriptionIds?.length ?? 0}</strong>
                </Typography>
                {(previewContent.processedLiftSettingsIds?.length ?? 0) > 0 && (
                  <Typography variant="subtitle2">
                    Lift Settings Updated: <strong>{previewContent.processedLiftSettingsIds?.length ?? 0}</strong>
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleProceed}>Proceed with Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resultOpen} onClose={() => setResultOpen(false)}>
        <DialogTitle>{resultContent.success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography color={resultContent.success ? 'success.main' : 'error.main'}>
            {resultContent.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
